import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Card, CardContent, CardMedia, Button, CircularProgress, Chip, Stack, IconButton } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { Workshop } from '../types/workshop';
import { styled } from '@mui/material/styles';
import ShareIcon from '@mui/icons-material/Share';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { SEOHead } from '../components/SEOHead';

const LevelChip = styled(Chip)(({ theme }) => ({
  borderRadius: '12px',
  fontWeight: 'bold',
  padding: '4px 12px',
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  backdropFilter: 'blur(10px)',
}));

const StyledCard = styled(Card)(() => ({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease-in-out',
  background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.15)',
  },
}));

const MotionGrid = motion(Grid);
const MotionChip = motion(Chip);
const MotionLevelChip = motion(LevelChip);
const MotionIconButton = motion(IconButton);

const floatingAnimation = {
  y: [0, -10, 0],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

const FilterSection = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(8px)',
  borderRadius: '24px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
}));

const FilterGroup = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '16px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const FilterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  fontSize: '1rem',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(1),
  display: 'block',
}));

const FilterChip = styled(Chip)(({ theme }) => ({
  borderRadius: '12px',
  padding: '6px 16px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  fontSize: '0.9rem',
  fontWeight: 500,
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(4px)',
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
  border: '1px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.1)',
    background: 'rgba(255, 255, 255, 0.15)',
    borderColor: theme.palette.primary.main,
  },
  '&.MuiChip-filled': {
    backgroundColor: 'rgba(33, 150, 243, 0.2)',
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(33, 150, 243, 0.3)',
    }
  }
}));

const MotionFilterChip = motion(FilterChip);

const chipVariants = {
  initial: { scale: 0.9, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  hover: { 
    scale: 1.05,
    y: -2,
    transition: { type: "spring", stiffness: 400 }
  },
  tap: { scale: 0.95 }
};

const filterGroupVariants = {
  initial: { opacity: 0, x: -20 },
  animate: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.5 }
  }
};

export const WorkshopsPage: React.FC = () => {
  const { t, currentLang } = useTranslation();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const isRTL = currentLang === 'he';

  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        const response = await api.get<{ data: Workshop[] }>('/workshops');
        setWorkshops(response.data.data);
        setError(null);
      } catch (err) {
        setError(t('pages.workshops.error'));
        console.error('Error fetching workshops:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshops();
  }, [t]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { 
      y: 50,
      opacity: 0,
      scale: 0.8,
      rotate: -5
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    },
    hover: {
      scale: 1.05,
      rotate: 2,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  const titleVariants = {
    hidden: { 
      y: -20,
      opacity: 0,
      scale: 0.9
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const filteredWorkshops = workshops.filter(workshop => {
    const matchesCategory = !selectedCategory || workshop.category === selectedCategory;
    const matchesLevel = !selectedLevel || workshop.level === selectedLevel;
    return matchesCategory && matchesLevel;
  });

  const categories = Array.from(new Set(workshops.map(w => w.category)));
  const levels = Array.from(new Set(workshops.map(w => w.level)));

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.2, 1],
            y: [0, -10, 0]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <CircularProgress size={60} />
        </motion.div>
        <motion.div
          animate={floatingAnimation}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: isRTL ? 'right' : 'left',
              color: 'text.secondary'
            }}
          >
            {t('pages.workshops.loading')}
          </Typography>
        </motion.div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          <Typography 
            color="error" 
            variant="h5"
            sx={{ textAlign: isRTL ? 'right' : 'left' }}
          >
            {error}
          </Typography>
        </motion.div>
      </Box>
    );
  }

  // SEO structured data for workshops page
  const workshopsStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": isRTL ? "סדנאות קריקט ועיצוב דיגיטלי" : "Cricut Workshops & Digital Design Classes",
    "description": isRTL 
      ? "סדנאות מקצועיות לקריקט, עיצוב דיגיטלי ותכשיטנות . למדו מהמומחית ענבל לוין בהרצליה."
      : "Professional workshops for Cricut, digital design, and jewelry making . Learn from expert Inbal Levin in Herzliya.",
    "url": "https://www.inbalart.co.il/workshops",
    "numberOfItems": workshops.length,
    "itemListElement": workshops.map((workshop, index) => ({
      "@type": "Course",
      "position": index + 1,
      "name": workshop.title,
      "description": workshop.description,
      "provider": {
        "@type": "Organization",
        "name": isRTL ? "ענבל ארט מייקרס" : "Inbal Art Makers",
        "url": "https://www.inbalart.co.il"
      },
      "courseMode": "blended",
      "educationalLevel": workshop.level,
      "inLanguage": isRTL ? "he" : "en",
      "offers": {
        "@type": "Offer",
        "price": workshop.price,
        "priceCurrency": "ILS",
        "availability": "https://schema.org/InStock"
      },
      "location": {
        "@type": "Place",
        "name": workshop.location,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": isRTL ? "הרצליה" : "Herzliya",
          "addressCountry": "IL"
        }
      }
    }))
  };

  return (
    <>
      <SEOHead 
        title={isRTL ? "סדנאות קריקט ועיצוב דיגיטלי  | ענבל ארט מייקרס" : "Cricut Workshops & Digital Design Classes  | Inbal Art Makers"}
        description={isRTL 
          ? "סדנאות מקצועיות לקריקט, עיצוב דיגיטלי ותכשיטנות . למדו מהמומחית ענבל לוין בהרצליה. Professional Cricut workshops ."
          : "Professional workshops for Cricut, digital design, and jewelry making . Learn from expert Inbal Levin in Herzliya. סדנאות קריקט מקצועיות ."
        }
        keywords={isRTL
          ? "סדנאות קריקט, קריקט ישראל, חיתוך דיגיטלי, תכשיטנות, עיצוב דיגיטלי, הרצליה, ענבל לוין, cricut workshops, digital design classes, jewelry making"
          : "cricut workshops, digital design classes, jewelry making, סדנאות קריקט, קריקט ישראל, חיתוך דיגיטלי, תכשיטנות, עיצוב דיגיטלי, הרצליה, ענבל לוין"
        }
        url="https://www.inbalart.co.il/workshops"
        structuredData={workshopsStructuredData}
      />
      <Box sx={{ 
        p: 3, 
        direction: isRTL ? 'rtl' : 'ltr',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
      <motion.div
        variants={titleVariants}
        initial="hidden"
        animate="visible"
      >
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{ 
            textAlign: isRTL ? 'right' : 'left',
            fontWeight: 'bold',
            mb: 2,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('pages.workshops.title')}
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          paragraph 
          sx={{ 
            textAlign: isRTL ? 'right' : 'left',
            maxWidth: '600px',
            mb: 4
          }}
        >
          {t('pages.workshops.subtitle')}
        </Typography>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <FilterSection>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <motion.div
                variants={filterGroupVariants}
                initial="initial"
                animate="animate"
              >
                <FilterGroup>
                  <FilterTitle>
                    {t('pages.workshops.categories.title')}
                  </FilterTitle>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {categories.map((category, index) => (
                      <MotionFilterChip
                        key={category}
                        label={t(`pages.workshops.categories.${category}`)}
                        onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                        color={selectedCategory === category ? 'primary' : 'default'}
                        variant={selectedCategory === category ? 'filled' : 'outlined'}
                        variants={chipVariants}
                        initial="initial"
                        animate="animate"
                        whileHover="hover"
                        whileTap="tap"
                        transition={{ delay: index * 0.05 }}
                      />
                    ))}
                  </Box>
                </FilterGroup>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div
                variants={filterGroupVariants}
                initial="initial"
                animate="animate"
                transition={{ delay: 0.2 }}
              >
                <FilterGroup>
                  <FilterTitle>
                    {t('pages.workshops.levels.title')}
                  </FilterTitle>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {levels.map((level, index) => (
                      <MotionFilterChip
                        key={level}
                        label={t(`pages.workshops.levels.${level}`)}
                        onClick={() => setSelectedLevel(selectedLevel === level ? null : level)}
                        color={selectedLevel === level ? 'primary' : 'default'}
                        variant={selectedLevel === level ? 'filled' : 'outlined'}
                        variants={chipVariants}
                        initial="initial"
                        animate="animate"
                        whileHover="hover"
                        whileTap="tap"
                        transition={{ delay: index * 0.05 }}
                      />
                    ))}
                  </Box>
                </FilterGroup>
              </motion.div>
            </Grid>
          </Grid>
        </FilterSection>
      </motion.div>

      <MotionGrid
        container
        spacing={4}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence>
          {filteredWorkshops.map((workshop) => (
            <MotionGrid
              item
              xs={12}
              md={6}
              lg={4}
              key={workshop._id}
              variants={cardVariants}
              whileHover="hover"
              layout
            >
              <StyledCard>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardMedia
                    component="img"
                    height="240"
                    image={workshop.image || '/images/workshop-placeholder.jpg'}
                    alt={workshop.title}
                    sx={{ objectFit: 'cover' }}
                  />
                </motion.div>
                <CardContent sx={{ textAlign: isRTL ? 'right' : 'left', p: 3 }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between' }}>
                    <Stack direction="row" spacing={4} sx={{ gap: 1 }}>
                      <MotionChip
                        label={t(`pages.workshops.categories.${workshop.category}`)}
                        color="primary"
                        variant="outlined"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        whileTap={{ scale: 0.95 }}
                      />
                      <MotionLevelChip
                        label={t(`pages.workshops.levels.${workshop.level}`)}
                        whileHover={{ scale: 1.1, rotate: -5 }}
                        whileTap={{ scale: 0.95 }}
                      />
                    </Stack>
                    <MotionIconButton
                      whileHover={{ scale: 1.2, rotate: -15 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <ShareIcon />
                    </MotionIconButton>
                  </Stack>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 'bold',
                      mb: 1
                    }}
                  >
                    {workshop.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    paragraph
                    sx={{ 
                      mb: 2,
                      minHeight: '60px'
                    }}
                  >
                    {workshop.description}
                  </Typography>
                  <Stack spacing={2} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationOnIcon color="primary" />
                      <Typography variant="body2" color="text.secondary">
                        {workshop.location}
                      </Typography>
                    </Stack>
                    <Stack direction="column" spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayIcon color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t('pages.workshops.dates')}:
                        </Typography>
                      </Stack>
                      <Stack direction="column" spacing={0.5} sx={{ pl: 2 }}>
                        {workshop.workshopDates.map((dateInfo, index) => (
                          <Typography 
                            key={index} 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            • {new Date(dateInfo.date).toLocaleDateString(currentLang, {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })} ({dateInfo.duration} {t('pages.workshops.minutes')})
                          </Typography>
                        ))}
                      </Stack>
                    </Stack>
                  </Stack>
                  {!workshop.shouldHidePrice && (
                    <Typography 
                      variant="h6" 
                      color="primary"
                      sx={{ mb: 2 }}
                    >
                      ₪{workshop.price}
                    </Typography>
                  )}
                  <motion.div
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      sx={{ 
                        borderRadius: '12px',
                        py: 1.5,
                        fontWeight: 'bold',
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                      }}
                    >
                      {t('pages.workshops.register')}
                    </Button>
                  </motion.div>
                </CardContent>
              </StyledCard>
            </MotionGrid>
          ))}
        </AnimatePresence>
      </MotionGrid>
      </Box>
    </>
  );
}; 